export const DEFAULT_LIST_STATUSES = ["staged", "approved", "failed"];

export const DELTA_DELETED = "__staged_config_deleted__";

export const LIST_DELTA_MARKER = "__staged_config_list_delta__";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const extractDeltaPaths = (delta, prefix = []) => {
  if (!isPlainObject(delta)) return [];
  if (delta[LIST_DELTA_MARKER]) {
    return Object.entries(delta[LIST_DELTA_MARKER].items ?? {}).flatMap(
      ([index, sub]) => {
        const path = [...prefix, Number(index)];
        const subPaths = extractDeltaPaths(sub, path);
        return subPaths.length > 0 ? subPaths : [path];
      }
    );
  }
  return Object.entries(delta).flatMap(([key, value]) => {
    const path = [...prefix, key];
    if (value === DELTA_DELETED) return [path];
    const subPaths = extractDeltaPaths(value, path);
    return subPaths.length > 0 ? subPaths : [path];
  });
};

const indexJsonLines = (jsonText) => {
  const lines = jsonText.split("\n");
  const stack = [];
  const entries = [];
  const keyPattern = /^"((?:[^"\\]|\\.)*)":\s?(.*)$/;
  lines.forEach((line, i) => {
    const lineNumber = i + 1;
    const trimmed = line.trim();
    if (trimmed === "") return;
    const top = stack[stack.length - 1];
    if (trimmed.startsWith("}") || trimmed.startsWith("]")) {
      stack.pop();
      return;
    }
    if (i === 0 && (trimmed === "{" || trimmed === "[")) {
      stack.push({
        type: trimmed === "{" ? "obj" : "arr",
        path: [],
        index: -1,
      });
      return;
    }
    if (top?.type === "obj") {
      const match = keyPattern.exec(trimmed);
      if (!match) return;
      const key = JSON.parse(`"${match[1]}"`);
      const path = [...top.path, key];
      entries.push({ path, lineNumber });
      const rest = match[2];
      if (rest === "{") stack.push({ type: "obj", path, index: -1 });
      else if (rest === "[") stack.push({ type: "arr", path, index: -1 });
      return;
    }
    if (top?.type === "arr") {
      top.index += 1;
      const path = [...top.path, top.index];
      entries.push({ path, lineNumber });
      if (trimmed === "{") stack.push({ type: "obj", path, index: -1 });
      else if (trimmed === "[") stack.push({ type: "arr", path, index: -1 });
    }
  });
  return entries;
};

export const findLinesForPaths = (jsonText, paths) => {
  if (!jsonText || !paths?.length) return [];
  const entries = indexJsonLines(jsonText);
  const isPrefix = (target, path) =>
    target.length <= path.length && target.every((part, i) => part === path[i]);
  const lines = entries
    .filter((entry) => paths.some((target) => isPrefix(target, entry.path)))
    .map((entry) => entry.lineNumber);
  return [...new Set(lines)].sort((a, b) => a - b);
};

export const parsePrNumbers = (input) => {
  const entries = (input || "").split(/[\s,]+/).filter((entry) => entry !== "");
  if (entries.length === 0) return null;
  const numbers = entries.map((entry) => {
    const cleaned = entry.replace(/^#/, "");
    if (!/^\d+$/.test(cleaned)) return null;
    return Number(cleaned);
  });
  if (numbers.some((n) => n === null || n <= 0)) return null;
  return numbers;
};

export const isValidGithubLink = (link) => {
  if (!link) return true;
  return link.startsWith("https://github.com/");
};

export const validateStageForm = ({ title, prInput, githubLink, reason }) => {
  const errors = {};
  const prNumbers = parsePrNumbers(prInput);
  if (!title || !title.trim()) errors.title = "Title is required";
  if (!prNumbers) errors.prInput = "Enter one or more PR numbers";
  if (!githubLink || !githubLink.trim())
    errors.githubLink = "GitHub link is required";
  else if (!isValidGithubLink(githubLink))
    errors.githubLink = "Must start with https://github.com/";
  if (!reason || !reason.trim()) errors.reason = "Reason is required";
  return {
    valid: Object.keys(errors).length === 0,
    errors,
    prNumbers,
  };
};

const STATUS_DISPLAY = {
  staged: { label: "Staged", color: "info" },
  approved: { label: "Approved", color: "success" },
  applied: { label: "Applied", color: "success" },
  rejected: { label: "Rejected", color: "default" },
  failed: { label: "Failed", color: "error" },
};

export const statusDisplay = (status) =>
  STATUS_DISPLAY[status] || { label: status, color: "default" };

export const summariseApplyResults = (results) => {
  const summary = { applied: 0, failed: 0, skipped: 0, failures: [] };
  for (const result of results) {
    if (result.outcome === "applied") summary.applied += 1;
    if (result.outcome === "skipped") summary.skipped += 1;
    if (result.outcome === "failed") {
      summary.failed += 1;
      summary.failures.push({
        id: result.id,
        key: result.key,
        reason: result.failureReason,
      });
    }
  }
  return summary;
};

export const approvedRefs = (records) =>
  records
    .filter((record) => record.status === "approved")
    .map((record) => ({ companyId: record.companyId, id: record.id }));
